import React, { useState } from 'react';
import { View } from 'react-native';
import { useLocalSearchParams, router } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert02Icon } from '@hugeicons-pro/core-stroke-rounded';

import { PhotosService } from '@/services/domain/PhotosService';
import { ImageViewer } from '@/components/ui/image-viewer/ImageViewer';
import { ROUTES } from '@/constants/routes';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { MessageView } from '@/components/ui/MessageView';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';

const returnToGlobalFeed = () => {
  requestAnimationFrame(() => {
    if (router.canDismiss()) {
      router.dismissAll();
    }

    router.replace(ROUTES.FEEDS_GLOBAL);
  });
};

const closePhotoPage = () => {
  requestAnimationFrame(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    returnToGlobalFeed();
  });
};

export default function PhotoPage() {
  const { guid } = useLocalSearchParams<{ guid: string }>();
  const [viewerVisible, setViewerVisible] = useState(true);
  const { t } = useTranslation();

  // Fetch photo data using React Query
  const {
    data: photo,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['photo', guid],
    queryFn: () => PhotosService.getPhotoDetails(guid!),
    enabled: !!guid,
    retry: (failureCount, queryError: any) => queryError?.status !== 404 && failureCount < 3,
  });

  // Handle viewer close
  const handleClose = () => {
    setViewerVisible(false);
    closePhotoPage();
  };

  if (!guid || isLoading) {
    return null;
  }

  if (error || !photo) {
    const errorMessage = error instanceof Error
      ? error.message
      : t('notFound.title');

    return (
      <View className="flex-1">
        <ScreenHeader
          options={{
            title: t('navigation.photo'),
            headerShown: true,
          }}
        />
        <MessageView
          type="error"
          text={t('common.error.general')}
          subText={errorMessage}
          icon={Alert02Icon}
        >
          <ButtonWrapper
            text={t('common.back')}
            variant="solid"
            action="primary"
            onPress={closePhotoPage}
            className="mt-4"
          />
        </MessageView>
      </View>
    );
  }

  return (
    <View className="flex-1">
      <ScreenHeader
        options={{
          title: photo
            ? t('photos.photoOf', 'Photo of {{userName}}', { userName: photo.Member.UserName })
            : t('navigation.photos'),
          headerShown: false,
        }}
      />
      <ImageViewer
        visible={viewerVisible}
        uri={photo.FullSizeUrl}
        photo={photo}
        onClose={handleClose}
        disableHistoryNavigation={true}
        allowImageDownload={!!photo.Member?.IsEvent}
      />
    </View>
  );
}
